/**
 * Main assessment container and state.
 */
import humps from 'lodash-humps';
import isArray from 'lodash/isArray';
import isInteger from 'lodash/isInteger';
import merge from 'lodash/merge';
import without from 'lodash/without';
import React, { Component, PropTypes } from 'react';
import ActiveComponent from './ActiveComponent';
/* global smoothScroll */

class Goal extends Component {

  constructor(props) {
    super(props);
    const { initialState } = this.props;
    if (initialState) {
      this.state = initialState;
    } else {
      if (window.location.hash.indexOf('category') === -1) {
        history.pushState('', document.title, window.location.pathname);
      }
      this.state = {
        componentData: this.props.componentData,
        currentPosition: 0,
        emailSent: false,
        goalCategories: this.props.goalCategories,
        goalEntity: this.props.goalEntity,
        lastCurrentPosition: 0,
        readingList: null,
        resultAccountableEmail: null,
        resultAccountableName: null,
        resultAccountableText: 'I just set up a goal to improve my wellbeing' +
          " on www.takingcharge.csh.umn.edu and I'm emailing you to keep me on track.",
        resultActionSteps: [null],
        resultCategory: null,
        resultHowOften: null,
        resultHowSoon: null,
        resultReadiness: null,
        resultsOverlay: false,
        resultStatedAffirmation: null,
        resultStatedChallenge: null,
        resultStatedChallengePlan: null,
        resultStatedGoal: null,
        resultUserEmail: null,
        resultUserName: null,
      };
    }
    this.addActionStep = this.addActionStep.bind(this);
    this.emailAccountableResults = this.emailAccountableResults.bind(this);
    this.emailResults = this.emailResults.bind(this);
    this.getParagraph = this.getParagraph.bind(this);
    this.getReadingList = this.getReadingList.bind(this);
    this.hashChangeHandler = this.hashChangeHandler.bind(this);
    this.hashSync = this.hashSync.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.previousStep = this.previousStep.bind(this);
    this.readinessLevels = [
      'maintenance',
      'action',
      'preparation',
      'contemplation',
      'pre-contemplation',
    ];
    this.setCategory = this.setCategory.bind(this);
    this.setResult = this.setResult.bind(this);
    this.updateActionStep = this.updateActionStep.bind(this);
    this.updateHash = this.updateHash.bind(this);
  }

  componentWillMount() {
    // Check if a category entity id was passed on mount, and if so, preset
    // the category.
    const initialHash = window.location.hash;
    if (initialHash.indexOf('category') > -1) {
      const categoryId = initialHash.split('=')[1];
      this.setCategory({
        id: categoryId,
        name: this.state.goalCategories[categoryId],
      });
    }
  }

  componentDidMount() {
    this.hashChangeHandler();
  }

  componentWillUpdate(nextProps, nextState) {
    const { currentPosition, lastCurrentPosition } = nextState;
    // Scroll to the top of the container if current and last scroll positions
    // aren't the same.
    if (isInteger(currentPosition) && isInteger(lastCurrentPosition)) {
      if (currentPosition !== lastCurrentPosition) {
        console.log('Updating position');
        const bodyPadding = parseInt(window.getComputedStyle(
          document.querySelector('body'), null,
        ).getPropertyValue('padding-top'), 10);
        const scrollOptions = {
          offset: bodyPadding + 10,
        };
        smoothScroll.animateScroll(
          document.getElementById('goal-app'),
          null,
          scrollOptions,
        );
        this.setState({
          lastCurrentPosition: currentPosition,
        });
      }
    }
    Goal.updateSessionState(nextState);
  }

  /**
   * Add a new value to the collection of action steps.
   */
  addActionStep() {
    const { resultActionSteps } = this.state;
    resultActionSteps.push(null);
    this.setState({
      resultActionSteps,
    });
  }

  /**
   * Notify drupal of changes in state.
   */
  static clearGoalResults() {
    sessionStorage.removeItem('goalTimestamp');
    sessionStorage.removeItem('goalData');
    window.location.reload(true);
  }

  /**
   * Email accountability person results.
   */
  emailAccountableResults() {
    const {
      resultAccountableEmail,
      resultAccountableName,
      resultAccountableText,
      resultActionSteps,
      resultHowOften,
      resultHowSoonText,
      resultStatedChallenge,
      resultStatedChallengePlan,
      resultStatedGoal,
      resultUserName,
    } = this.state;
    const combinedActionSteps = without(resultActionSteps, null).map(step =>
      `<li>${step.stepValue} by ${step.timeValue}</li>`,
    ).join('');
    // Send email to user's accountability person
    if (resultAccountableEmail) {
      console.log(`Sending email to ${resultAccountableEmail}`);
      const postAccountableBody = JSON.stringify({
        email: resultAccountableEmail,
        monitorInterval: resultHowOften ? resultHowOften.value : null,
        myAccountablePerson: resultAccountableName || '',
        myActionSteps: `<ul>${combinedActionSteps}</ul>`,
        myChallengePlan: resultStatedChallengePlan,
        myChallenges: resultStatedChallenge,
        myGoal: resultStatedGoal,
        myHowSoon: resultHowSoonText,
        myName: resultUserName || '',
        myText: resultAccountableText,
        subject: 'Goal setting results',
        template: 'f42ccab1-2324-4846-a133-24cce87b73e6',
      });
      fetch('/api/email/send.json?_format=json', {
        method: 'POST',
        body: postAccountableBody,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      })
      .then(response => response.json())
      .then((result) => {
        console.log(result);
      });
    }
  }

  /**
   * Email goal results.
   */
  emailResults() {
    const {
      readingList,
      resultAccountableName,
      resultActionSteps,
      resultHowOften,
      resultHowSoonText,
      resultStatedAffirmation,
      resultStatedChallenge,
      resultStatedChallengePlan,
      resultStatedGoal,
      resultUserEmail,
      resultUserName,
    } = this.state;
    const combinedActionSteps = without(resultActionSteps, null).map(step =>
      `<li>${step.stepValue} by ${step.timeValue}</li>`,
    ).join('');
    const postBody = JSON.stringify({
      email: resultUserEmail,
      monitorInterval: resultHowOften ? resultHowOften.value : null,
      myAccountablePerson: resultAccountableName,
      myActionSteps: `<ul>${combinedActionSteps}</ul>`,
      myAffirmation: resultStatedAffirmation,
      myChallengePlan: resultStatedChallengePlan,
      myChallenges: resultStatedChallenge,
      myGoal: resultStatedGoal,
      myHowSoon: resultHowSoonText,
      myReadingList: readingList,
      myName: resultUserName,
      subject: 'Goal setting results',
      template: '1f0eb89f-b5d8-4adc-aedf-c06bb65a41fe',
    });
    // Send email to the user
    console.log(`Sending email to ${resultUserEmail}`);
    fetch('/api/email/send.json?_format=json', {
      method: 'POST',
      body: postBody,
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    })
    .then(response => response.json())
    .then((result) => {
      this.setState({
        emailSent: true,
      });
      console.log(result);
    });
  }

  /**
   * Paragraph components are mostly unique types, so split into separate
   * React components and return the correct one based on the vid.
   * @TODO: don't pass 2,000,000 props, especially multiples of the same value.
   */
  getParagraph() {
    const {
      componentData,
      currentPosition,
      emailSent,
      goalCategories,
      readingList,
      resultAccountableEmail,
      resultAccountableName,
      resultAccountableText,
      resultActionSteps,
      resultCategory,
      resultHowOften,
      resultHowSoon,
      resultHowSoonText,
      resultReadiness,
      resultsOverlay,
      resultStatedAffirmation,
      resultStatedChallenge,
      resultStatedChallengePlan,
      resultStatedGoal,
      resultUserEmail,
      resultUserName,
    } = this.state;
    const componentType = componentData[currentPosition] ?
      componentData[currentPosition].type[0].targetId : '';
    const component = (
      <ActiveComponent
        addActionStep={this.addActionStep}
        clearGoalResults={Goal.clearGoalResults}
        componentType={componentType}
        emailAccountableResults={this.emailAccountableResults}
        emailResults={this.emailResults}
        emailSent={emailSent}
        goalCategories={goalCategories}
        monitorInterval={resultHowOften ? resultHowOften.value : null}
        myAccountablePerson={resultAccountableName}
        myActionSteps={without(resultActionSteps, null)}
        myAffirmation={resultStatedAffirmation}
        myChallengePlan={resultStatedChallengePlan}
        myChallenges={resultStatedChallenge}
        myGoal={resultStatedGoal}
        myReadingList={readingList}
        nextStep={this.nextStep}
        paragraphData={componentData[currentPosition]}
        previousStep={this.previousStep}
        readingList={readingList}
        resultAccountableEmail={resultAccountableEmail}
        resultAccountableName={resultAccountableName}
        resultAccountableText={resultAccountableText}
        resultActionSteps={resultActionSteps}
        resultCategory={resultCategory}
        resultHowOften={resultHowOften}
        resultHowSoon={resultHowSoon}
        resultHowSoonText={resultHowSoonText}
        resultReadiness={resultReadiness}
        resultsOverlay={resultsOverlay}
        resultStatedAffirmation={resultStatedAffirmation}
        resultStatedChallenge={resultStatedChallenge}
        resultStatedChallengePlan={resultStatedChallengePlan}
        resultStatedGoal={resultStatedGoal}
        resultUserEmail={resultUserEmail}
        resultUserName={resultUserName}
        setCategory={this.setCategory}
        setResult={this.setResult}
        stateOfReadiness={this.readinessLevels[this.state.resultReadiness]}
        updateActionStep={this.updateActionStep}
      />
    );
    return component;
  }

  /**
   * Retrieve the reading list once we have the aspect id
   */
  getReadingList(entityId) {
    fetch(`/api/goals/reading/${entityId}?_format=json`, { credentials: 'same-origin' })
    .then(response => response.json())
    .then((readingListData) => {
      const readingList = humps(readingListData);
      this.setState({
        readingList,
      });
    })
    .catch((ex) => {
      console.log('parsing failed', ex);
    });
  }

  /**
   * Check to ensure the hash value and the currentPosition are synced.
   */
  hashChangeHandler() {
    // history.pushState('', document.title, window.location.pathname);
    this.hashSync();
    window.addEventListener('hashchange', this.hashSync);
  }

  hashSync() {
    const { currentPosition } = this.state;
    const currentHash = window.location.hash;
    // If there isn't a hash, we should be on the intro screen
    if (currentHash === '' && currentPosition !== 0) {
      this.setState({
        currentPosition: 0,
      });
    } else {
      const hashPath = currentHash.split('/');
      // If the path is for a question, make sure that the index and the
      // hash are synced.
      if (hashPath[1] === 'step') {
        const hashIndex = parseInt(hashPath[2], 10);
        if (isInteger(hashIndex) && hashIndex !== currentPosition) {
          this.setState({
            currentPosition: hashIndex,
          });
        }
      // If the hash reflects results, make sure the index is equal to the
      // length of the questions array.
      } else if (hashPath[1] === 'results') {
        if (currentPosition !== this.state.questions.length) {
          this.setState({
            currentPosition: this.state.questions.length,
          });
        }
      }
    }
  }

  /**
   * Increment the current position in the assessment.
   * -1 is the start, otherwise the step counter is the index in the questions
   * array.
   */
  nextStep() {
    const { currentPosition } = this.state;
    const newIndex = currentPosition + 1;
    this.updateHash(newIndex);
    this.setState({
      currentPosition: newIndex,
    });
  }

  /**
   * Increment the current position in the assessment.
   * -1 is the start, otherwise the step counter is the index in the questions
   * array.
   */
  previousStep() {
    const { currentPosition } = this.state;
    const newIndex = currentPosition - 1;
    this.updateHash(newIndex);
    this.setState({
      currentPosition: newIndex,
    });
  }

  /**
   * Set category based on the target value of a select.
   */
  setCategory(entity) {
    this.setState({
      resultCategory: entity,
    });
    this.getReadingList(parseInt(entity.id, 10));
  }

  /**
   * General function for setting state values in parent component.
   */
  setResult(result) {
    this.setState({ ...result });
  }

  /**
   * Update the value of the action step at the nth index.
   */
  updateActionStep(index, updateValue) {
    this.setState((previousState) => {
      const { resultActionSteps } = previousState;
      resultActionSteps[index] = merge(resultActionSteps[index], updateValue);
      return { resultActionSteps };
    });
  }

  /**
   * Update the hash when the index changes.
   */
  updateHash(newIndex) {
    const { componentData } = this.state;
    if (newIndex === 0 && (window.location.hash.indexOf('category') === -1)) {
      history.pushState('', document.title, window.location.pathname);
    } else if (isArray(componentData)) {
      window.location.hash = `/step/${newIndex}`;
    }
  }

  /**
   * Update session state and timestamp.
   */
  static updateSessionState(nextState) {
    sessionStorage.goalData = JSON.stringify(nextState);
    sessionStorage.goalTimestamp = Math.floor(Date.now() / 1000);
  }

  render() {
    const activeComponent = this.getParagraph();
    return (
      <div>
        {activeComponent}
      </div>
    );
  }
}

Goal.propTypes = {
  componentData: PropTypes.arrayOf(PropTypes.shape({})),
  goalCategories: PropTypes.shape({}),
  goalEntity: PropTypes.arrayOf(PropTypes.shape({})),
  initialState: PropTypes.shape({}),
};

export default Goal;



// WEBPACK FOOTER //
// js/src/goals/Goal.js